// src/mobile/MobileResourceCard.js
// Mobile-optimized resource card with touch interactions

import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Star, Image as ImageIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import './styles/mobile-resource-card.css';

// Format date for display
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

// Calculate reading time
const calculateReadingTime = (content) => {
  if (!content) return '3 min read';
  const wordCount = content.split(/\s+/).length;
  const minutes = Math.ceil(wordCount / 200);
  return `${minutes} min read`;
};

// Type badge labels
const TYPE_LABELS = {
  article: 'Article',
  caseStudy: 'Case Study',
  news: 'News',
};

const MobileResourceCard = ({
  resource,
  variant = 'default', // 'default' | 'compact'
  index = 0,
}) => {
  const {
    slug,
    type,
    title,
    summary,
    heroImage,
    category,
    publishedAt,
    topics = [],
    author,
    isFeatured,
    readingTime,
    metrics = [],
  } = resource;

  // Determine link path based on type
  const linkPath = type === 'caseStudy'
    ? `/case-studies/${slug}`
    : `/articles/${slug}`;

  const displayTags = topics.slice(0, 1);
  const overflowCount = topics.length - 1;

  const cardClasses = [
    'mobile-resource-card',
    variant === 'compact' && 'mobile-resource-card--compact',
    isFeatured && 'mobile-resource-card--featured',
    type === 'caseStudy' && 'mobile-resource-card--case-study',
  ].filter(Boolean).join(' ');

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.4,
        delay: index * 0.08,
        ease: [0.22, 1, 0.36, 1],
      }}
    >
      <Link to={linkPath} className={cardClasses}>
        {/* Image Section */}
        <div className="mobile-resource-card__image-wrapper">
          {heroImage?.url ? (
            <img
              src={heroImage.url}
              alt={heroImage.alt || title}
              className="mobile-resource-card__image"
              loading="lazy"
            />
          ) : (
            <div className="mobile-resource-card__image-placeholder">
              <ImageIcon />
            </div>
          )}

          {/* Type Badge */}
          <span className={`mobile-resource-card__badge mobile-resource-card__badge--${type}`}>
            {TYPE_LABELS[type] || type}
          </span>

          {/* Featured Indicator */}
          {isFeatured && (
            <div className="mobile-resource-card__featured">
              <Star />
              <span>Featured</span>
            </div>
          )}
        </div>

        {/* Content Section */}
        <div className="mobile-resource-card__content">
          {/* Meta Line */}
          <div className="mobile-resource-card__meta">
            {category && (
              <>
                <span className="mobile-resource-card__category">{category}</span>
                <span className="mobile-resource-card__meta-divider" />
              </>
            )}
            <span>{formatDate(publishedAt)}</span>
          </div>

          {/* Title */}
          <h3 className="mobile-resource-card__title">{title}</h3>

          {/* Summary (hidden in compact mode) */}
          {variant !== 'compact' && summary && (
            <p className="mobile-resource-card__summary">{summary}</p>
          )}

          {/* Tags (hidden in compact mode) */}
          {variant !== 'compact' && displayTags.length > 0 && (
            <div className="mobile-resource-card__tags">
              {displayTags.map((tag) => (
                <span key={tag.id || tag.name} className="mobile-resource-card__tag">
                  {tag.name}
                </span>
              ))}
              {overflowCount > 0 && (
                <span className="mobile-resource-card__tag-overflow">
                  +{overflowCount}
                </span>
              )}
            </div>
          )}

          {/* Footer (hidden in compact mode) */}
          {variant !== 'compact' && (
            <div className="mobile-resource-card__footer">
              {author ? (
                <div className="mobile-resource-card__author">
                  {author.avatarUrl && (
                    <img
                      src={author.avatarUrl}
                      alt={author.name}
                      className="mobile-resource-card__author-avatar"
                    />
                  )}
                  <div className="mobile-resource-card__author-info">
                    <span className="mobile-resource-card__author-name">
                      {author.name}
                    </span>
                    <span className="mobile-resource-card__reading-time">
                      {readingTime || calculateReadingTime(summary)}
                    </span>
                  </div>
                </div>
              ) : (
                <span className="mobile-resource-card__reading-time">
                  {readingTime || calculateReadingTime(summary)}
                </span>
              )}
              <div className="mobile-resource-card__arrow">
                <ArrowRight size={16} />
              </div>
            </div>
          )}
        </div>

        {/* Case Study Metrics (if applicable) */}
        {type === 'caseStudy' && metrics.length > 0 && variant !== 'compact' && (
          <div className="mobile-resource-card__metrics">
            {metrics.slice(0, 3).map((metric, idx) => (
              <div key={idx} className="mobile-resource-card__metric">
                <div className="mobile-resource-card__metric-value">
                  {metric.value}
                </div>
                <div className="mobile-resource-card__metric-label">
                  {metric.label}
                </div>
              </div>
            ))}
          </div>
        )}
      </Link>
    </motion.div>
  );
};

export default MobileResourceCard;
